import {
  Avatar,
  Button,
  Flex,
  Layout,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Popconfirm,
  Image,
  Form,
  Modal,
  Input,
  Select,
} from "antd";
import type { TableProps, GetProp } from "antd";
import { MediaDto } from "dtos/media.dto";
import { TbPhoto } from "react-icons/tb";
import { Link } from "react-router-dom";
import { ApiService } from "api/api.service";
import { useState } from "react";

import { TbTrash, TbEdit } from "react-icons/tb";
import type { PopconfirmProps } from "antd";
import { useAppSelector } from "store";
import { RoleTypes } from "dtos/user.dto";

// type TablePaginationConfig = Exclude<GetProp<TableProps, "pagination">, boolean>;

const ListView = () => {
  const currentUser = useAppSelector((state) => state.user);

  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
  });
  const deleteMedia = ApiService.media.useDeleteByIdOrSlug();
  const editMedia = ApiService.media.useEditMedia();
  const mediaList = ApiService.media.useGetAllMediaPaginated({ page: filters.page, limit: filters.limit });
  const [selectedMedia, setSelectedMedia] = useState("");
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingMedia, setEditingMedia] = useState<Partial<MediaDto>>({});
  const [form] = Form.useForm();

  const columns: TableProps<MediaDto>["columns"] = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 350,
      ellipsis: true,
      render: (value: string, record: MediaDto, index: number) => {
        const defaultImage = "https://www.contentviewspro.com/wp-content/uploads/2017/07/default_image.png";
        return (
          <Flex gap={8} align="center">
            <Image
              className="rounded-lg object-contain"
              width={100}
              height={60}
              src={record?.url ? record.url : defaultImage}
              fallback={defaultImage}
              alt={record.title}
              placeholder={
                <div className="bg-neutral-100 text-neutral-400 flex items-center justify-center">
                  <TbPhoto />
                </div>
              }
            />
            <Flex vertical>
              <Typography.Text strong>{record.title}</Typography.Text>
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: "Caption",
      dataIndex: "caption",
      key: "caption",
      width: 200,
      ellipsis: true,
      // align: "center",
      render: (value: string, record: MediaDto, index: number) => {
        return (
          <Typography.Paragraph type="secondary" className="w-[250px]">
            {record.caption?.substring(0, 30)}
            {record.caption?.length > 30 ? "..." : ""}
          </Typography.Paragraph>
        );
      },
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      width: 250,
      // ellipsis: true,
      render: (value: string, record: MediaDto, index: number) => {
        return record?.tags?.map((tag, i) => {
          return <Tag key={`tag-${i}`}>{tag}</Tag>;
        });
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      align: "start",
      width: 150,
      render: (name: string, record: MediaDto, index: number) => {
        return (
          <Space>
            <Button icon={<TbEdit />} type="text" onClick={() => openEditModal(record)} />
            {currentUser?.role ? (
              [RoleTypes.SUPERADMIN, RoleTypes.ADMIN, RoleTypes.CONTENT_ADMIN].includes(currentUser?.role) ? (
                <Popconfirm
                  title="Delete the Media Item"
                  description="Are you sure to delete this media item?"
                  onConfirm={confirm}
                  onCancel={cancel}
                  okButtonProps={{}}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button onClick={() => setSelectedMedia(record._id)} type="text">
                    <TbTrash style={{ color: "red", fontSize: "20px" }} />
                  </Button>
                </Popconfirm>
              ) : null
            ) : null}
          </Space>
        );
      },
    },
  ];

  // function for delete
  const confirm: PopconfirmProps["onConfirm"] = (e) => {
    deleteMedia.mutate(selectedMedia, {
      onSuccess: () => {
        message.success("media deleted successfullt");
        mediaList.refetch();
      },
      onError: () => {
        message.error("Operation failed !!");
      },
    });
  };
  const cancel: PopconfirmProps["onCancel"] = (e) => {};

  // Functions for Edit Modal
  const openEditModal = (media: MediaDto) => {
    setEditingMedia(media);
    setSelectedMedia(media._id);
    setIsEditModalVisible(true);
    form.setFieldsValue({
      title: media.title,
      caption: media.caption,
      tags: media.tags,
      type: media.type,
    });
  };

  const handleEditSubmit = () => {
    form.validateFields().then((values) => {
      const updatedMedia = { ...values, id: selectedMedia }; // Assuming selectedMedia holds the ID of the media to be edited
      console.log(updatedMedia);
      editMedia.mutate(updatedMedia, {
        onSuccess: () => {
          message.success("Media updated successfully");
          setIsEditModalVisible(false);
          mediaList.refetch(); // Refetch list to reflect updated changes
        },
        onError: () => {
          message.error("Failed to update media.");
        },
      });
    });
  };

  return (
    <>
      <Table
        virtual
        loading={mediaList.isLoading}
        columns={columns}
        dataSource={mediaList?.data?.data}
        pagination={{
          showSizeChanger: true,
          total: mediaList.data?.totalObjects,
          pageSize: filters.limit,
          onChange: (page, pageSize) => {
            setFilters({
              page: page,
              limit: pageSize,
            });
          },
        }}
      />

      <Modal
        title="Edit Media"
        open={isEditModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => setIsEditModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Title" name="title" rules={[{ required: true, message: "Please input the title!" }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Caption" name="caption">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Tags" name="tags">
            <Select mode="tags" style={{ width: "100%" }} placeholder="Tags" />
          </Form.Item>
          <Form.Item label="Type" name="type" rules={[{ required: true, message: "Please select a type!" }]}>
            <Select placeholder="Select a type">
              <Select.Option value="IMAGE">IMAGE</Select.Option>
              <Select.Option value="VIDEO">VIDEO</Select.Option>
              <Select.Option value="AUDIO">AUDIO</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ListView;
